import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Approval.css";

const PAYMENT_API = "https://salathai.co.tz/mrms/Payment.php";

export default function Approval() {
  const token = localStorage.getItem("token");
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdowns, setCountdowns] = useState({});

  const fetchPendingPayments = async () => {
    try {
      const res = await fetch(`${PAYMENT_API}?action=pending`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const pending = data.filter(p => p.status === 'PENDING');
        setPayments(pending);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingPayments();
    const interval = setInterval(fetchPendingPayments, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdowns(prev => {
        const newCountdowns = {};
        Object.entries(prev).forEach(([id, time]) => {
          if (time > 1) newCountdowns[id] = time - 1;
        });
        return newCountdowns;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const approvePayment = async (paymentId, orderNumber, amount, paymentMethod) => {
    const result = await Swal.fire({
      title: 'Approve Payment?',
      html: `
        <div style="text-align: left;">
          <p><strong>Order #:</strong> ${orderNumber}</p>
          <p><strong>Amount:</strong> ${parseFloat(amount).toLocaleString()} TZS</p>
          <p><strong>Payment Method:</strong> ${paymentMethod}</p>
          <p><strong>Approved by:</strong> ${staff.full_name}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Approve',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        confirmButton: 'swal-confirm-btn',
        cancelButton: 'swal-cancel-btn'
      },
      showClass: {
        popup: 'animate__animated animate__fadeInUp animate__faster'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutDown animate__faster'
      }
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Processing...',
        html: 'Please wait while we process the approval',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
        background: '#ffffff',
        customClass: {
          popup: 'swal-loading-popup'
        }
      });

      try {
        const res = await fetch(`${PAYMENT_API}`, {
          method: "PUT",
          headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json" 
          },
          body: JSON.stringify({ 
            action: "approve", 
            id: paymentId 
          })
        });

        if (res.ok) {
          const data = await res.json();
          
          setCountdowns(prev => ({ ...prev, [paymentId]: 10 }));
          setPayments(prev => prev.filter(p => p.id !== paymentId));
          
          Swal.fire({
            title: 'Payment Approved',
            html: `
              <div style="text-align: left; padding: 10px 0;">
                <p><strong>Order #:</strong> ${orderNumber}</p>
                <p><strong>Amount:</strong> ${parseFloat(amount).toLocaleString()} TZS</p>
                <p><strong>Approved by:</strong> ${data.approved_by || staff.full_name}</p>
                <p><strong>Status:</strong> <span style="color: #10b981;">Completed</span></p>
              </div>
            `,
            icon: 'success',
            confirmButtonColor: '#10b981',
            confirmButtonText: 'Done',
            timer: 3000,
            timerProgressBar: true,
            background: '#ffffff',
            customClass: {
              popup: 'swal-success-popup',
              confirmButton: 'swal-success-btn'
            },
            showClass: {
              popup: 'animate__animated animate__tada'
            }
          });
          
          setTimeout(fetchPendingPayments, 1000);
        } else {
          const error = await res.json();
          Swal.fire({
            title: 'Approval Failed',
            text: error.error || "Approval failed. Please try again.",
            icon: 'error',
            confirmButtonColor: '#ef4444',
            confirmButtonText: 'OK',
            background: '#ffffff',
            customClass: {
              popup: 'swal-error-popup',
              confirmButton: 'swal-error-btn'
            },
            showClass: {
              popup: 'animate__animated animate__shakeX'
            }
          });
        }
      } catch (err) {
        console.error("Approve error:", err);
        Swal.fire({
          title: 'Network Error',
          text: "Please check your connection and try again.",
          icon: 'error',
          confirmButtonColor: '#ef4444',
          confirmButtonText: 'OK',
          background: '#ffffff',
          customClass: {
            popup: 'swal-error-popup',
            confirmButton: 'swal-error-btn'
          },
          showClass: {
            popup: 'animate__animated animate__shakeX'
          }
        });
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getPaymentMethodClass = (method) => {
    const classes = {
      'CASH': 'method-cash',
      'M-PESA': 'method-mpesa', 
      'MIX BY YAS': 'method-mix',
      'SELECOM': 'method-selecom',
      'CRDB BANK': 'method-crdb'
    };
    return classes[method] || 'method-default';
  };

  if (loading) {
    return (
      <div className="approval-container">
        <div className="loading">Loading payments...</div>
      </div>
    );
  }

  return (
    <div className="approval-container">
      <style>{`
        /* SweetAlert2 Custom Styles - Inline */
        .swal-custom-popup {
          border-radius: 15px !important;
          padding: 20px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2) !important;
        }
        
        .swal-custom-title {
          font-size: 24px !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
        }
        
        .swal-confirm-btn {
          background: #10b981 !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          padding: 10px 24px !important;
          transition: transform 0.2s !important;
        }
        
        .swal-confirm-btn:hover {
          transform: translateY(-2px) !important;
        }
        
        .swal-cancel-btn {
          background: #ef4444 !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          padding: 10px 24px !important;
          transition: transform 0.2s !important;
        }
        
        .swal-cancel-btn:hover {
          transform: translateY(-2px) !important;
        }
        
        .swal-loading-popup {
          border-radius: 15px !important;
          background: #667eea !important;
          color: white !important;
        }
        
        .swal-success-popup {
          border-radius: 15px !important;
          border-left: 5px solid #10b981 !important;
        }
        
        .swal-error-popup {
          border-radius: 15px !important;
          border-left: 5px solid #ef4444 !important;
        }
        
        .swal-success-btn, .swal-error-btn {
          border-radius: 8px !important;
          font-weight: 600 !important;
          padding: 10px 24px !important;
        }
        
        .swal-success-btn {
          background: #10b981 !important;
        }
        
        .swal-error-btn {
          background: #ef4444 !important;
        }
        
        .animate__animated {
          animation-duration: 0.5s;
        }
        
        .animate__fadeInUp {
          animation-name: fadeInUp;
        }
        
        .animate__fadeOutDown {
          animation-name: fadeOutDown;
        }
        
        .animate__tada {
          animation-name: tada;
        }
        
        .animate__shakeX {
          animation-name: shakeX;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate3d(0, 100%, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        
        @keyframes fadeOutDown {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
            transform: translate3d(0, 100%, 0);
          }
        }
        
        @keyframes tada {
          from {
            transform: scale3d(1, 1, 1);
          }
          10%, 20% {
            transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
          }
          30%, 50%, 70%, 90% {
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
          }
          40%, 60%, 80% {
            transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
          }
          to {
            transform: scale3d(1, 1, 1);
          }
        }
        
        @keyframes shakeX {
          from, to {
            transform: translate3d(0, 0, 0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translate3d(-10px, 0, 0);
          }
          20%, 40%, 60%, 80% {
            transform: translate3d(10px, 0, 0);
          }
        }
      `}</style>
      
      <header className="approval-header">
        <div className="header-left">
          <h1>Payment Approval</h1>
          <div className="cashier-info">
            <span className="cashier-label">Cashier:</span>
            <span className="cashier-name">{staff.full_name}</span>
          </div>
        </div>
        <div className="header-right">
          <div className="pending-badge">
            <span className="badge-count">{payments.length}</span>
            <span className="badge-label">Pending</span>
          </div>
        </div>
      </header>

      <div className="approval-list-container">
        {payments.length === 0 ? (
          <div className="empty-state">
            <h3>No Pending Payments</h3>
            <p>All payments are cleared</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="payments-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Time</th>
                  <th>Waiter</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => {
                  const countdown = countdowns[payment.id] || 0;
                  const isApproving = countdown > 0;
                  
                  return (
                    <tr key={payment.id} className={isApproving ? 'approving-row' : ''}>
                      <td className="order-cell">
                        <div className="order-number">{payment.order_number}</div>
                      </td>
                      
                      <td className="time-cell">
                        {formatTime(payment.created_at)}
                      </td>
                      
                      <td className="waiter-cell">
                        {payment.staff_name}
                      </td>
                      
                      <td className="method-cell">
                        <div className={`method-wrapper ${getPaymentMethodClass(payment.payment_method)}`}>
                          <span className="method-text">{payment.payment_method}</span>
                        </div>
                      </td>
                      
                      <td className="amount-cell">
                        <div className="amount-main">
                          {parseFloat(payment.total_amount).toLocaleString()} TZS
                        </div>
                        {payment.discount_amount > 0 && (
                          <div className="amount-discount">
                            -{parseFloat(payment.discount_amount).toLocaleString()}
                          </div>
                        )}
                      </td>
                      
                      <td className="action-cell">
                        {isApproving ? (
                          <div className="approving-status">
                            <span className="approving-text">Approved</span>
                            <span className="countdown-text">({countdown}s)</span>
                          </div>
                        ) : (
                          <button 
                            className="approve-button"
                            onClick={() => approvePayment(
                              payment.id, 
                              payment.order_number, 
                              payment.total_amount, 
                              payment.payment_method
                            )}
                            disabled={isApproving}
                          >
                            <span className="button-text">Approve</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}